import React, { useEffect, useRef } from 'react';
import { Rect, Transformer, Text } from 'react-konva';

// A component for an editable rectangle (bounding box).
const EditableRect = ({ shapeProps, isSelected, onSelect, onChange, color, showTiltAngles }) => {
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current) {
      // Attach transformer when selected.
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  const handleTiltClick = (e) => {
    // Stop event propagation so it only selects the current element and not parent elements
    e.stopPropagation();
    onSelect();
  }
  // Transformer Properties for better visibility/usability
  const transformerProps = {
      // Use basic square anchors
      anchorStroke: color || 'dodgerblue',
      anchorFill: 'white',
      anchorSize: 8,
      borderStroke: color || 'dodgerblue',
      borderStrokeWidth: 1,
      borderDash: [3, 3],
      keepRatio: false, // Allow free resizing
      rotateEnabled: false, // Usually not needed for BBoxes
       boundBoxFunc:(oldBox, newBox) => {
          // Limit resize so it can't become too small.
          const minWidth = 5;
          const minHeight = 5;
          // Prevent negative dimensions
          newBox.width = Math.max(minWidth, newBox.width);
          newBox.height = Math.max(minHeight, newBox.height);

          // Optional: Add boundary checks against image dimensions if needed here
          // const imageWidth = shapeRef.current?.getStage()?.width();
          // const imageHeight = shapeRef.current?.getStage()?.height();
          // ... check newBox.x, newBox.y, newBox.width, newBox.height against image boundaries ...

          return newBox;
       }
  };


  return (
    <>
      <Rect
        onClick={onSelect}
        onTap={onSelect}
        ref={shapeRef}
        {...shapeProps} // Includes x, y, width, height
        draggable
        stroke={isSelected ? color || 'dodgerblue' : color} // Highlight stroke when selected
        strokeWidth={isSelected ? 3 : 2} // Thicker stroke when selected
        onDragStart={onSelect} // Select on drag start as well
        onDragEnd={(e) => {
           // Update position immediately on drag end for responsiveness
           // But the final normalized conversion happens in the parent state update
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y()
          });
        }}
         onTransformStart={onSelect} // Select on transform start
        onTransformEnd={(e) => {
          // Konva recommends resetting scale after transform for stability
          const node = shapeRef.current;
          if (!node) return;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();

          // Reset scale
          node.scaleX(1);
          node.scaleY(1);

          const newWidth = Math.max(5, node.width() * scaleX);
          const newHeight = Math.max(5, node.height() * scaleY);

          onChange({
            ...shapeProps, // Keep original ID etc.
            x: node.x(),
            y: node.y(),
            width: newWidth,
            height: newHeight,
          });
        }}
        
        // Add hover effect
        onMouseEnter={e => {
            const stage = e.target.getStage();
            if (stage) stage.container().style.cursor = 'pointer';
        }}
        onMouseLeave={e => {
            const stage = e.target.getStage();
            if (stage) stage.container().style.cursor = 'default';
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
         {...transformerProps}
        />
      )}
      {isSelected && showTiltAngles && shapeProps.tilt_angle !== null && (
        <Text
          x={shapeProps.x + 5} // Adjust position as needed
          y={shapeProps.y + shapeProps.height + 5} // Adjust position as needed
          text={`Tilt: ${shapeProps.tilt_angle}°`}
          fontSize={12}
          fill={color || 'dodgerblue'}
          onClick={handleTiltClick}
        />
      )}
    </>
  );
};

export default EditableRect;
